/**
 * ReferralCodeField — the optional referral/promo-code input for a signup card.
 *
 * Extracted verbatim from RegisterPage (2026-07-16) when the page split into
 * per-method signup cards, so the email and Google cards can each render the
 * field. Presentational only: all state lives in useReferralField, and both
 * cards render the SAME binding from ONE hook call, which is what keeps the two
 * fields in sync and stops them ever disagreeing about a code's validity.
 *
 * `id` is REQUIRED and has no default on purpose. This component is rendered
 * twice on one page, and BOTH the Label's htmlFor AND the error element's
 * aria-describedby target bind to it — a shared default would produce duplicate
 * DOM ids and point the Google card's label and error description at the email
 * card's field. Pass a distinct id per card; the error id is derived as
 * `${id}-error` so it stays unique too.
 *
 * promo-code US-005 (T3): this field GATES BOTH DOORS. When `field.isInvalid`
 * is true it shows a blocking error and the page disables both Sign Up and
 * Continue with Google. That deliberately supersedes BR-TS015.1 for the
 * settled-invalid case only (FR-2.8/AC-005.6) — see useReferralField.
 */

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { OTTER_INPUT_CLASS } from '@/lib/formStyles';
import { CODE_INVALID_MSG, type ReferralFieldBinding } from '@/hooks/useReferralField';

export interface ReferralCodeFieldProps {
  /** Unique per rendered instance — e.g. 'referralCode' | 'googleReferralCode'. */
  id: string;
  /** Binding from useReferralField(). Sharing one binding across two fields syncs them. */
  field: ReferralFieldBinding;
  disabled?: boolean;
}

export function ReferralCodeField({ id, field, disabled }: ReferralCodeFieldProps) {
  const errorId = `${id}-error`;

  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-otter-earth-800">
        Referral Code (optional)
      </Label>
      <Input
        id={id}
        type="text"
        placeholder="e.g. THINK-A7X2"
        value={field.code}
        onChange={(e) => field.onChange(e.target.value)}
        disabled={disabled}
        // BUG-20260702-205850: readOnly (not disabled) so a valid URL-prefilled
        // code stays visible and is still submitted, but cannot be altered.
        readOnly={field.isLocked}
        aria-readonly={field.isLocked}
        // promo-code US-005 (T3) / AC-005.5: WCAG 2.1 AA — associate the
        // blocking error with the field via aria-describedby and flag the field
        // itself as invalid to assistive tech (non-colour-only cue). Only set
        // when the code is actually blocking.
        aria-invalid={field.isInvalid}
        aria-describedby={field.isInvalid ? errorId : undefined}
        className={cn(
          OTTER_INPUT_CLASS,
          field.isLocked && 'bg-gray-50 text-gray-600 cursor-not-allowed',
          field.isInvalid && 'border-red-400 focus-visible:border-red-500'
        )}
      />
      {field.isLocked ? (
        <p className="text-xs text-green-700" role="status" aria-live="polite">
          Referral code applied from your invite link.
        </p>
      ) : (
        <p className="text-xs text-gray-500">
          Got a referral code from a friend? Enter it here (optional).
        </p>
      )}
      {/* promo-code US-005 (T3) / AC-005.1 / AC-005.5: the settled-invalid
          blocking error takes priority over the advisory notice below so the
          user never sees two conflicting messages for the same outcome (they
          are mutually exclusive — 'invalid' drives this branch, every other
          outcome falls through to the advisory notice). role="alert" +
          aria-live="assertive" announces it immediately to assistive tech
          (WCAG 2.1 AA). Dash-free copy per house style. */}
      {field.isInvalid ? (
        <p id={errorId} role="alert" aria-live="assertive" className="text-xs text-red-600 font-medium">
          {CODE_INVALID_MSG}
        </p>
      ) : (
        // TS-015 / T4: advisory-only gentle notice — muted amber, NOT the red
        // error style, and never gates submit (BR-TS015.1, FR-2.5). Still shown
        // for the fail-open 'undecidable'/legacy advisory path.
        field.notice && (
          <p className="text-xs text-amber-700" role="status" aria-live="polite">
            {field.notice}
          </p>
        )
      )}
    </div>
  );
}
